/**
 * @module utils/auditMessages
 * @description Generadores de mensajes descriptivos para el registro de auditoría.
 *
 * Convierte valores técnicos (estados, fechas, prioridades, asignaciones) en texto
 * legible en español para mostrar en el `AuditTrail` del sistema.
 *
 * Cada función toma los valores anteriores y nuevos de un campo y devuelve
 * una frase que el auditor puede leer sin necesidad de conocer los enums internos.
 *
 * ## Funciones disponibles
 * | Función | Ejemplo de salida |
 * |---------|------------------|
 * | `createStatusChangeMessage()` | `Cambió el estado de "No Iniciado" a "Completado"` |
 * | `createDueDateChangeMessage()` | `Cambió fecha de "sin fecha" a "15 de junio de 2026"` |
 * | `createPriorityChangeMessage()` | `Cambió la prioridad de "Media" a "Crítica"` |
 * | `createAssignmentMessage()` | `Asignó el control a Juan Pérez, María García` |
 * | `createProgressMessage()` | `Actualizó el progreso de 40% a 85%` |
 * | `createAuditMessage()` | Wrapper que elige la función correcta según `actionType` |
 *
 * @see AuditFormatters para las constantes de iconos y colores del trail visual
 * @see DataContext para cómo se persisten los mensajes en `audit_trail`
 */

// Mapeo de estados a texto en español
const STATUS_LABELS: Record<string, string> = {
  not_started: 'No Iniciado',
  in_progress: 'En Progreso',
  review: 'En Revisión',
  completed: 'Completado',
  non_compliant: 'No Conforme'
};

// Mapeo de prioridades a texto en español
const PRIORITY_LABELS: Record<string, string> = {
  low: 'Baja',
  medium: 'Media',
  high: 'Alta',
  critical: 'Crítica'
};

/**
 * Genera un mensaje descriptivo para cambio de estado
 */
export function createStatusChangeMessage(
  previousStatus: string,
  newStatus: string
): string {
  const prevLabel = STATUS_LABELS[previousStatus] || previousStatus;
  const newLabel = STATUS_LABELS[newStatus] || newStatus;

  return `Cambió el estado de "${prevLabel}" a "${newLabel}"`;
}

/**
 * Genera un mensaje descriptivo para cambio de fecha de vencimiento
 */
export function createDueDateChangeMessage(
  previousDate: string | null,
  newDate: string | null
): string {
  const formatDate = (date: string | null) => {
    if (!date) return 'sin fecha';
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const prevLabel = formatDate(previousDate);
  const newLabel = formatDate(newDate);

  if (!previousDate) {
    return `Estableció fecha de vencimiento: ${newLabel}`;
  }

  return `Modificó la fecha de vencimiento de ${prevLabel} a ${newLabel}`;
}

/**
 * Genera un mensaje descriptivo para cambio de prioridad
 */
export function createPriorityChangeMessage(
  previousPriority: string,
  newPriority: string
): string {
  const prevLabel = PRIORITY_LABELS[previousPriority] || previousPriority;
  const newLabel = PRIORITY_LABELS[newPriority] || newPriority;

  return `Cambió la prioridad de "${prevLabel}" a "${newLabel}"`;
}

/**
 * Genera un mensaje descriptivo para asignación de referentes
 */
export function createAssignmentMessage(
  referents: { id: string; name: string; email: string }[]
): string {
  if (referents.length === 0) {
    return 'Removió todas las asignaciones del control';
  }

  if (referents.length === 1) {
    return `Asignó el control a ${referents[0].name}`;
  }

  const names = referents.map(r => r.name);
  const lastReferent = names.pop();

  return `Asignó el control a ${names.join(', ')} y ${lastReferent}`;
}

/**
 * Genera un mensaje descriptivo para creación de tarea
 */
export function createTaskCreatedMessage(
  taskTitle: string,
  assignee: { name: string; email: string },
  priority: string,
  dueDate?: string
): string {
  const priorityLabel = PRIORITY_LABELS[priority] || priority;

  let message = `Creó la tarea "${taskTitle}" asignada a ${assignee.name}`;

  if (priority === 'critical' || priority === 'high') {
    message += ` con prioridad ${priorityLabel}`;
  }

  if (dueDate) {
    const dateLabel = new Date(dueDate).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    message += `, vence el ${dateLabel}`;
  }

  return message;
}

/**
 * Genera un mensaje descriptivo para subida de evidencia
 */
export function createEvidenceUploadMessage(
  evidenceName: string,
  evidenceType: string,
  fileSize: number
): string {
  const sizeLabel = formatFileSize(fileSize);
  const typeLabel = getEvidenceTypeLabel(evidenceType);

  return `Subió ${typeLabel} "${evidenceName}" (${sizeLabel})`;
}

/**
 * Genera un mensaje descriptivo para eliminación de evidencia
 */
export function createEvidenceDeleteMessage(
  evidenceName: string,
  evidenceType: string
): string {
  const typeLabel = getEvidenceTypeLabel(evidenceType);

  return `Eliminó ${typeLabel} "${evidenceName}"`;
}

/**
 * Genera un mensaje descriptivo para completar tarea
 */
export function createTaskCompletedMessage(
  taskTitle: string
): string {
  return `Completó la tarea "${taskTitle}"`;
}

/**
 * Genera un mensaje descriptivo para actualizar tarea
 */
export function createTaskUpdatedMessage(
  taskTitle: string,
  changes: string[]
): string {
  if (changes.length === 0) {
    return `Actualizó la tarea "${taskTitle}"`;
  }

  return `Actualizó la tarea "${taskTitle}": ${changes.join(', ')}`;
}

// ===== FUNCIONES AUXILIARES =====

/**
 * Formatea el tamaño de archivo a formato legible
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Obtiene la etiqueta legible para el tipo de evidencia
 */
function getEvidenceTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    document: 'el documento',
    screenshot: 'la captura de pantalla',
    video: 'el video',
    other: 'el archivo'
  };

  return labels[type] || 'el archivo';
}

/**
 * Genera un mensaje genérico descriptivo basado en el tipo de acción
 */
export function createDescriptiveMessage(
  actionType: string,
  action: string,
  previousValue?: unknown,
  newValue?: unknown
): string {
  // Si ya hay un mensaje personalizado, usarlo
  if (action && !action.startsWith('Cambió') && !action.startsWith('Modificó')) {
    return action;
  }

  // Generar mensaje basado en el tipo de acción
  switch (actionType) {
    case 'status_change':
      if (typeof previousValue === 'string' && typeof newValue === 'string') {
        return createStatusChangeMessage(previousValue, newValue);
      }
      break;

    case 'due_date_change':
      return createDueDateChangeMessage(
        previousValue as string | null,
        newValue as string | null
      );

    case 'priority_change':
      if (typeof previousValue === 'string' && typeof newValue === 'string') {
        return createPriorityChangeMessage(previousValue, newValue);
      }
      break;
  }

  // Fallback al mensaje original
  return action;
}
